import PageContent from "../../../layout/PageContent";
import {useNavigate, useParams} from "react-router-dom";
import {Box, Button, Chip, FormControlLabel, LinearProgress, Switch} from "@mui/material";
import {TitleText} from "../../../layout/Typography";
import * as React from "react";
import {useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {useRecoilValue} from "recoil";
import {CurrentUserIsAdmin, UserSimplified} from "../../../atoms/CurrentUser";
import {SavingUI} from "../../../SavingProvider";
import {FormInputText} from "../../shop/wholesale/form/FormInputText";
import {
   BrandHouseShopFragment,
   EnumIntegrationConnectorRefType,
   UpdateByIdBrandHouseInput,
   useDetailsPageBrandByIdQuery,
   useDetailsPageBrandUpdateMutation
} from "../../../Queries";
import SettingsHeader from "../SettingsHeader"
import {FrontendIcon} from "./FrontendIcon";
import {ShopCreatePath} from "./ShopCreate";
import {ShopSettingsPath} from "./ShopSettings";
import {ShopIntegrationsPagePath} from "./ShopIntegrations";
import {RefIntegrations} from "./RefIntegrations";
import {BrandIntegrationSettingsPath} from "./BrandIntegrationSettings";
import {PageCard, PageCards} from "../../../layout/PageCards";
import {AddressEditor} from "../AddressEditor";
import {ConfigCompareDialog} from "./ConfigCompareDialog";
import {FormInputSelect} from "../../shop/wholesale/form/FormInputSelect";

export const DetailsPagePath = (brandHouseId: string) => {
   return "/brands/" + brandHouseId + "/settings/brand"
}

export const DetailsPage = () => {
   const {brandHouseId} = useParams<{ brandHouseId: string }>()
   const [updateBrand] = useDetailsPageBrandUpdateMutation();
   const navigate = useNavigate();
   const [configCompareOpen, setConfigCompareOpen] = useState(false);
   const isAdmin = useRecoilValue(CurrentUserIsAdmin);
   const isSimplified = useRecoilValue(UserSimplified);
   const canToggleBrandActive = isAdmin && !isSimplified;

   const {data: brandData} = useDetailsPageBrandByIdQuery({
      variables: {
         id: brandHouseId
      }
   });

   if (!brandData) {
      return <LinearProgress/>
   }

   const onSubmit = async (data: UpdateByIdBrandHouseInput) => {
      await SavingUI.process(async () => {
         await updateBrand({
            variables: {
               id: brandHouseId,
               input: {
                  name: data.name,
                  address: data.address,
                  ...(isAdmin ? {logisticsPartnerId: data.logisticsPartnerId} : {})
               }
            }
         })
      }, 'Brand updated')
   }

   if (!brandData?.BrandHouse) {
      return <LinearProgress/>
   }

   const onGoToShop = (shop: BrandHouseShopFragment) => {
      navigate(ShopSettingsPath(brandHouseId, shop._id))
   }

   const onGotoConnector = (connectorId: string) => {
      navigate(BrandIntegrationSettingsPath(brandHouseId, connectorId))
   }

   const onToggleBrandActive = async (checked: boolean) => {
      await SavingUI.process(async () => {
         await updateBrand({
            variables: {
               id: brandHouseId,
               input: {
                  deactivated: !checked
               }
            },
            refetchQueries: ['DetailsPageBrandById']
         })
      }, checked ? 'Brand activated' : 'Brand deactivated')
   }

   return <div>
      <SettingsHeader title={"Brand and shops"}>
         {canToggleBrandActive && (
            <FormControlLabel
               control={
                  <Switch
                     checked={!brandData.BrandHouse.deactivated}
                     onChange={(e) => onToggleBrandActive(e.target.checked)}
                     size="small"
                     color="primary"
                  />
               }
               label="Brand active"
            />
         )}
      </SettingsHeader>
      <PageContent>
         <Box>
            <BrandForm data={brandData?.BrandHouse as UpdateByIdBrandHouseInput}
                       logisticsPartners={brandData.LogisticsPartners}
                       showLogisticsPartner={isAdmin}
                       onSave={onSubmit}/>
            <Button sx={{marginTop: '10px'}} type="submit" form={"brandEditForm"} variant={"contained"}>Update</Button>
         </Box>
      </PageContent>
      <Box sx={{marginTop: '40px'}}>
         <PageCards cards={(brandData?.BrandHouse?.shops || []).map(shop => ({
               id: shop!._id,
               icon: <FrontendIcon type={shop!.frontendType}/>,
               name: shop!.name || 'Unknown shop',
               description: shop!.externalId,
               onClick: () => onGoToShop(shop as BrandHouseShopFragment),
               commands: <Box sx={{display: 'flex', columnGap: '15px', alignItems: 'center'}}>
                  {shop!.live ?
                     <Chip color={"success"} label={"Live"} size={"small"} sx={{minWidth: '80px'}}/> :
                     <Chip color={"secondary"} label={"Offline"} size={"small"} sx={{minWidth: '80px'}}/>}
                  <Button onClick={(e: any) => {
                     e.preventDefault();
                     e.stopPropagation();
                     navigate(ShopIntegrationsPagePath(brandHouseId, shop!._id))
                  }} color={"secondary"} variant={"outlined"}>Integrations</Button>
               </Box>,
            } as PageCard
         ))}
                    title={"Shops"}
                    commands={
                       <>
                          <Button
                             onClick={() => setConfigCompareOpen(true)}
                             variant={"outlined"}
                             color={"secondary"}
                             size={"small"}
                             sx={{ml: 2}}
                             disabled={!brandData?.BrandHouse?.shops || brandData.BrandHouse.shops.length === 0}
                          >
                             Check Configs
                          </Button>
                          <Button
                             onClick={() => navigate(ShopCreatePath(brandHouseId))}
                             variant={"outlined"}
                             color={"secondary"}
                             size={"small"}
                             sx={{ml: 2}}
                          >
                             Add shop
                          </Button>
                       </>
                    }
         />
      </Box>
      <Box sx={{marginTop: '40px'}}>
         <RefIntegrations onNavigateTo={onGotoConnector} refId={brandHouseId}
                          title={"Brand integrations"}
                          refType={EnumIntegrationConnectorRefType.BRANDHOUSE}/>
      </Box>
      <ConfigCompareDialog
         open={configCompareOpen}
         onClose={() => setConfigCompareOpen(false)}
         brandHouseId={brandHouseId!}
         shops={(brandData?.BrandHouse?.shops || [])
            .filter((s): s is NonNullable<typeof s> => s != null)
            .map(s => ({_id: s._id, name: s.name}))}
      />
   </div>
}

type BrandFormProps = {
   data: UpdateByIdBrandHouseInput
   logisticsPartners: {_id: any, name?: string | null}[]
   showLogisticsPartner: boolean
   onSave: (data: UpdateByIdBrandHouseInput) => Promise<void>
}

const BrandForm = ({data, logisticsPartners, showLogisticsPartner, onSave}: BrandFormProps) => {
   const methods = useForm<UpdateByIdBrandHouseInput>({
      defaultValues: data
   });

   const {handleSubmit} = methods;

   const onSubmit = async (data: UpdateByIdBrandHouseInput) => {
      await onSave(data);
   }

   return <div>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
         <form id={"brandEditForm"} onSubmit={handleSubmit(onSubmit)}>
            <FormProvider {...methods}>
               <div>
                  <TitleText type={"subtitle1"}>The name of the brand</TitleText>
                  <FormInputText name={"name"} required label={"Name of brand"}/>
                  {showLogisticsPartner && <>
                     <TitleText type={"subtitle1"}>Logistics partner</TitleText>
                     <FormInputSelect name={"logisticsPartnerId"}
                                      required
                                      label={"Logistics partner"}
                                      options={logisticsPartners.map(partner => ({
                                         id: partner._id,
                                         name: partner.name || "Unnamed logistics partner"
                                      }))}/>
                  </>}
                  <TitleText type={"subtitle1"}>License key</TitleText>
                  <FormInputText disabled name={"license"} required label={"Key"}/>
                  <AddressEditor addressField={"address"}/>
               </div>
            </FormProvider>
         </form>
      </Box>
   </div>
}
